import { useAtomValue } from 'jotai'
import { TypeIcon } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { WidgetInputComplexNumericField, WidgetLabel, WidgetOptInCheckbox } from '../../../../rapid-ui'
import { externalCanvasDataAtom } from '../../atoms'
import { useCanvasStore } from '../../canvas-store'
import type { Layer, TextStyle } from '../../data/v1'
import { useDesignerActions } from '../../use-designer-actions'
import { ImageCropQuickTool } from './image-crop'
import { QuickToolButton, QuickToolPopover } from './primitives'

function TypographyPanel({ layerId, textStyle }: { layerId: string; textStyle: TextStyle }) {
	const { updateLayer } = useDesignerActions()

	const change = useCallback((updates: Partial<TextStyle>, transient?: boolean) => {
		updateLayer(layerId, (prev) => {
			if (!('textStyle' in prev)) { return {} }
			return { textStyle: { ...prev.textStyle, ...updates } }
		}, transient ? { transient: true } : undefined)
	}, [layerId, updateLayer])

	const onFontSizeChange = useCallback((fontSizePxValue: number) => change({ fontSizePxValue }), [change])
	const onFontSizeTransient = useCallback((fontSizePxValue: number) => change({ fontSizePxValue }, true), [change])
	const onFontWeightChange = useCallback((fontWeightValue: number) => change({ fontWeightValue }), [change])
	const onFontWeightTransient = useCallback((fontWeightValue: number) => change({ fontWeightValue }, true), [change])
	const onLineHeightChange = useCallback((lineHeightValue: number) => change({ lineHeightValue }), [change])
	const onLineHeightTransient = useCallback((lineHeightValue: number) => change({ lineHeightValue }, true), [change])

	const onFontSizeOptIn = useCallback(() => change({ fontSizeMode: 'value' }), [change])
	const onFontSizeOptOut = useCallback(() => change({ fontSizeMode: 'auto' }), [change])
	const onFontWeightOptIn = useCallback(() => change({ fontWeightMode: 'value' }), [change])
	const onFontWeightOptOut = useCallback(() => change({ fontWeightMode: 'auto' }), [change])
	const onLineHeightOptIn = useCallback(() => change({ lineHeightMode: 'value' }), [change])
	const onLineHeightOptOut = useCallback(() => change({ lineHeightMode: 'auto' }), [change])

	return (
		<div className="flex flex-col gap-2 p-2" style={{ width: 220 }}>
			<WidgetLabel label="Font size">
				<WidgetOptInCheckbox
					optedIn={textStyle.fontSizeMode === 'value'}
					onOptIn={onFontSizeOptIn}
					onOptOut={onFontSizeOptOut}
				>
					<WidgetInputComplexNumericField
						value={textStyle.fontSizePxValue}
						onChange={onFontSizeChange}
						onTransientChange={onFontSizeTransient}
						min={1}
						defaultValue={16}
						suffix="px"
					/>
				</WidgetOptInCheckbox>
			</WidgetLabel>

			<WidgetLabel label="Font weight">
				<WidgetOptInCheckbox
					optedIn={textStyle.fontWeightMode === 'value'}
					onOptIn={onFontWeightOptIn}
					onOptOut={onFontWeightOptOut}
				>
					<WidgetInputComplexNumericField
						value={textStyle.fontWeightValue}
						onChange={onFontWeightChange}
						onTransientChange={onFontWeightTransient}
						min={100}
						max={900}
						step={100}
						sensitivity={0.05}
						defaultValue={400}
					/>
				</WidgetOptInCheckbox>
			</WidgetLabel>

			<WidgetLabel label="Line height">
				<WidgetOptInCheckbox
					optedIn={textStyle.lineHeightMode === 'value'}
					onOptIn={onLineHeightOptIn}
					onOptOut={onLineHeightOptOut}
				>
					<WidgetInputComplexNumericField
						value={textStyle.lineHeightValue}
						onChange={onLineHeightChange}
						onTransientChange={onLineHeightTransient}
						min={0.5}
						max={5}
						step={0.01}
						defaultValue={1.4}
					/>
				</WidgetOptInCheckbox>
			</WidgetLabel>
		</div>
	)
}

type ActivePanel = 'typography' | 'crop' | null

export function TextQuickTools(
	{ layer, layerEl }: {
		layer: Extract<Layer, { type: 'text' }>
		layerEl: HTMLElement | null
	},
) {
	const store = useCanvasStore()
	const externalData = useAtomValue(externalCanvasDataAtom, { store })
	const [activePanel, setActivePanel] = useState<ActivePanel>(null)
	const typoBtnRef = useRef<HTMLButtonElement>(null)

	function toggle(panel: ActivePanel) {
		setActivePanel((prev) => prev === panel ? null : panel)
	}

	return (
		<>
			<QuickToolButton
				buttonRef={typoBtnRef}
				active={activePanel === 'typography'}
				onClick={() => toggle('typography')}
				title="Typography"
			>
				<TypeIcon size={14} />
			</QuickToolButton>

			<ImageCropQuickTool
				layerId={layer.id}
				active={activePanel === 'crop'}
				onToggle={() => toggle('crop')}
				background={layer.background}
				externalBackgroundUrl={externalData.backgroundUrl}
				layerEl={layerEl}
			/>

			<QuickToolPopover
				anchorEl={typoBtnRef.current}
				open={activePanel === 'typography'}
				onClose={() => setActivePanel(null)}
			>
				<TypographyPanel layerId={layer.id} textStyle={layer.textStyle} />
			</QuickToolPopover>
		</>
	)
}
